import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchData } from "../../../services/apiService";
import {
  getLocalTzOffsetMinutes,
  localCalendarDateToYyyymmdd,
} from "../../../services/dateUtils";
import { showSnackbar } from "../../../components/snackbar/Snackbar.slice";

const PERIODIC_ACQUIRER_REPORT_GENERATE =
  "periodicAcquirerReport/generateAcquirerReport";

export const generateAcquirerReport = createAsyncThunk(
  PERIODIC_ACQUIRER_REPORT_GENERATE,
  async (
    {
      isoID,
      currency,
      reportStartDate,
      reportEndDate,
      token,
      acquirerData,
      onSuccessCallback = false,
      storeAcquirerId,
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      // const url = "/report/acquirer-iso-periodic-clearing-settlement";
      const url = "/acquirer-iso-periodic-clearing-settlement";
      const maxRetries = 3;
      let attempts = 0;
      let success = false;
      let retryCount = 0;
      const effectiveAcquirerID = storeAcquirerId || acquirerData.acquirerId;

      const timestamp = new Date().toISOString();
      const data = {
        version: "04.09.01",
        ...(isoID && { isoID }),
        acquirer: {
          callbackURL:
            "https://webhook.site/dd4eadec-dab5-4397-9754-c45738f2fb9e",
          timeStamp: timestamp,
        },
        fiat: { currencyCode: currency },
        report: {
          // Fechas de calendario local; el back agrupa por día local usando tzOffsetMinutes.
          startDate: localCalendarDateToYyyymmdd(reportStartDate),
          endDate: localCalendarDateToYyyymmdd(reportEndDate),
          tzOffsetMinutes: getLocalTzOffsetMinutes(reportStartDate),
        },
      };

      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        acquirerName: acquirerData.acquirerName,
        acquirerID: storeAcquirerId || acquirerData.acquirerId,
      };

      while (attempts < maxRetries && !success) {
        try {
          attempts++;
          retryCount = attempts;

          const response = await fetchData({
            endpoint: url,
            data,
            headers,
            token,
          });

          const isos = response?.data?.isos;
          const hasIsos = Array.isArray(isos) && isos.length > 0;
          const consolidated = response?.data?.consolidated;
          const hasConsolidated =
            consolidated && Object.keys(consolidated).length > 0;

          if (hasIsos || hasConsolidated) {
            dispatch(
              showSnackbar({
                message: "Report generated successfully!",
                severity: "success",
              }),
            );
            success = true;

            if (onSuccessCallback) {
              onSuccessCallback();
            }

            return {
              acquirerID: response.data.acquirerID ?? effectiveAcquirerID,
              acquirerName: acquirerData.acquirerName,
              retryCount,
              isos: isos ?? [],
              consolidatedData: consolidated ?? null,
            };
          } else {
            throw new Error("No valid data received from server");
          }
        } catch (error) {
          if (attempts < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 4000));
          }
        }
      }

      if (!success) {
        dispatch(
          showSnackbar({
            message: "No transaction for this date",
            severity: "error",
          }),
        );
        return rejectWithValue({
          message: "No transaction for this date" || "Error generating report",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Error generating report",
      });
    }
  },
);
